import type { I18nDictionaryShape } from '../../types';
import type { featuresEnGb } from '../en-GB/features';

export const featuresPlPl = {
  annotations: {
    aiFeedbackAlert: {
      desktopPrompt: 'Kliknij zdjęcie prawym przyciskiem myszy, aby przekazać opinię dla AI!',
      improveAccuracy: 'Popraw dokładność.',
      improveAccuracyFor: 'Popraw dokładność dla {{tradeCompanyName}}.',
      mobilePrompt: 'Dotknij i przytrzymaj zdjęcie, aby przekazać opinię dla AI!',
    },
    aiFeedbackSwitch: {
      label: 'Pokaż opinie AI',
    },
    aiFilters: {
      analysisFailedDescription: 'Nasz zespół się tym zajmie. W międzyczasie przejdź do pozostałych zdjęć w raporcie.',
      analysisFailedTitle: 'AI nie mogła przeanalizować tego zdjęcia',
      description: 'Pokaż lub ukryj kontury materiału i porównaj je z oryginalnym zdjęciem.',
      noFiltersDescription:
        'AI Safi zaznacza kontury tylko materiału niedocelowego (takiego jak zanieczyszczenia lub elementy dodatkowe). To zdjęcie zawiera wyłącznie materiał docelowy (np. UBC lub PET), dlatego nie ma konturów ani filtrów do wyświetlenia.',
      noFiltersTitle: 'Brak filtrów do wyświetlenia, ponieważ wykryto 100% materiału docelowego',
      showOutlines: 'Pokaż kontury AI',
      title: 'Dostosuj filtry AI',
    },
    commentBubble: {
      actions: {
        deleteFeedback: 'Usuń opinię',
        edit: 'Edytuj',
      },
      adminMeta: 'ID:{{commentId}} AI:{{aiVersion}}',
      deleteDialog: {
        confirm: 'Usuń',
        message: 'Czy na pewno chcesz usunąć tę opinię? Tej operacji nie można cofnąć.',
        title: 'Usuń opinię',
      },
    },
    commentFormDrawer: {
      actions: {
        cancel: 'Anuluj',
        save: 'Zapisz',
      },
      feedbackType: {
        incorrectDetection: 'Wykryto nieprawidłowy obiekt',
        missingDetection: 'Nie wykryto obiektu',
      },
      labels: {
        note: 'Notatka',
      },
      titleCreate: 'Przekaż opinię dla AI',
      titleEdit: 'Edytuj opinię dla AI',
    },
    helpAndFeedbackBar: {
      downloadFullImageAriaLabel: 'Pobierz pełny obraz dowodu',
    },
    segmentationOverlay: {
      ariaLabel: 'Obraz z nakładką segmentacji',
      helpText: {
        desktop: 'Najedź na zdjęcie, aby powiększyć',
        mobile: 'Powiększ palcami',
      },
    },
    speechBubbleAvatar: {
      viewCommentAriaLabel: 'Zobacz komentarz od {{fullName}}',
    },
  },
  auth: {
    companySignUpForm: {
      actions: {
        next: 'Dalej',
      },
      labels: {
        companyType: 'Typ firmy',
        country: 'Kraj',
        name: 'Nazwa',
      },
      notifications: {
        errorSigningUp: 'Błąd podczas rejestracji',
      },
      validation: {
        companyTypeRequired: 'Typ firmy jest wymagany',
        countryRequired: 'Kraj jest wymagany',
        nameRequired: 'Nazwa jest wymagana',
      },
    },
    otpForm: {
      actions: {
        signIn: 'Zaloguj się',
      },
      labels: {
        passcode: 'Kod',
      },
      validation: {
        digitsOnly: 'Może zawierać tylko cyfry',
        passcodeLength: 'Kod jednorazowy musi mieć 4 cyfry',
        passcodeRequired: 'Kod jednorazowy jest wymagany',
      },
    },
    signInEmailForm: {
      actions: {
        next: 'Dalej',
      },
      labels: {
        email: 'E-mail',
      },
      links: {
        signInWithPhone: 'Zaloguj się numerem telefonu',
      },
      validation: {
        emailRequired: 'E-mail jest wymagany',
        invalidEmail: 'Nieprawidłowy e-mail',
        termsRequired: 'Musisz zaakceptować regulamin',
      },
    },
    signInPage: {
      subtitle:
        'Zaloguj się do {{brandName}} za pomocą służbowego adresu e-mail lub numeru telefonu. Wyślemy Ci kod jednorazowy, aby przyspieszyć dostęp.',
      title: 'Kontrola jakości z pewnością',
    },
    signInPhoneForm: {
      actions: {
        next: 'Dalej',
        signInWithEmail: 'Zaloguj się e-mailem',
      },
      labels: {
        phoneNumber: 'Numer telefonu',
      },
      validation: {
        phoneNumberRequired: 'Numer telefonu jest wymagany',
      },
    },
    termsAndPolicyAgreement: {
      fullSentence:
        'Dołączając do {{brandName}}, akceptuję <termsLink>Regulamin</termsLink> oraz <privacyLink>Politykę prywatności</privacyLink>.',
    },
    userSignUpForm: {
      actions: {
        next: 'Dalej',
      },
      labels: {
        email: 'E-mail',
        fullName: 'Imię i nazwisko',
        phoneNumber: 'Numer telefonu',
      },
      notifications: {
        errorSigningUp: 'Błąd podczas rejestracji',
      },
      validation: {
        emailRequired: 'E-mail jest wymagany',
        fullNameRequired: 'Imię i nazwisko są wymagane',
        invalidEmail: 'Nieprawidłowy e-mail',
        phoneNumberRequired: 'Numer telefonu jest wymagany',
        termsRequired: 'Musisz zaakceptować regulamin',
      },
    },
  },
  backlog: {
    breakdowns: {
      evaluatedSpecsItems: {
        ungroupedMaterials: 'Materiały niezgrupowane',
      },
      formatDirtScore: {
        average: 'Średnio',
        clean: 'Czysty',
        dirty: 'Brudny',
        notAvailable: 'Nd.',
        veryClean: 'Bardzo czysty',
      },
      materialAiBreakdown: {
        aiModelNotSupported: 'Model AI nieobsługiwany',
      },
      materialLabels: {
        aluminium: 'Aluminium',
        aluminiumAerosol: 'Aerozol aluminiowy',
        aluminiumFoodCan: 'Aluminiowa puszka spożywcza',
        brown: 'Brązowy',
        clear: 'Przezroczysty',
        clearFullSleeveNonFoodGrade: 'Przezroczysty pełny sleeve, niespożywczy',
        clearNonFoodGrade: 'Przezroczysty, niespożywczy',
        clearPetTray: 'Przezroczysta tacka PET',
        darkBlue: 'Ciemnoniebieski',
        dirtyBottles: 'Brudne butelki',
        expandedPolystyrene: 'Polistyren spieniony',
        film: 'Folia',
        foilAndTray: 'Folia i tacka',
        fullSleeve: 'Pełny sleeve',
        green: 'Zielony',
        hdpe: 'HDPE',
        hdpeBlack: 'Czarny',
        hdpeBlue: 'Niebieski',
        hdpeBrown: 'Brązowy',
        hdpeGreen: 'Zielony',
        hdpeJazz: 'Jazz',
        hdpeNaturalFoodGrade: 'Naturalny spożywczy',
        hdpeNaturalNonFoodGrade: 'Naturalny niespożywczy',
        hdpeOrange: 'Pomarańczowy',
        hdpePurple: 'Fioletowy',
        hdpeRed: 'Czerwony',
        hdpeSiliconTubes: 'Tuby silikonowe',
        hdpeWhiteFoodGrade: 'Biały spożywczy',
        hdpeWhiteNonFoodGrade: 'Biały niespożywczy',
        hdpeYellow: 'Żółty',
        lightBlue: 'Jasnoniebieski',
        metal: 'Metal',
        nonAluminium: 'Niealuminiowe',
        nonHdpe: 'Nie-HDPE',
        nonMetal: 'Niemetalowe',
        nonPet: 'Nie-PET',
        nonPlastic: 'Nieplastikowe',
        oilBottle: 'Butelka po oleju',
        opaque: 'Nieprzezroczysty',
        other: 'Inne',
        otherAluminium: 'Inne aluminium',
        otherColour: 'Inny kolor',
        otherMetal: 'Inny metal',
        otherPlastic: 'Inny plastik',
        pet: 'PET',
        petTrayContaminatedClear: 'Zanieczyszczona przezroczysta tacka PET',
        polypropylene: 'PP',
        prohibitive: 'Niedozwolone',
        total: 'Razem',
        ubc: 'UBC',
        waste: 'Odpady',
        white: 'Biały',
      },
      representativeEvidenceFilter: {
        label: 'Pokaż obrazy:',
        options: {
          bestBales: 'Najlepsze bele',
          typicalBales: 'Typowe bele',
          worstBales: 'Najgorsze bele',
        },
      },
      representativeEvidenceSection: {
        distributionTitle: 'Rozkład {{targetName}}',
      },
      specificationAlerts: {
        groupDescription:
          '<boldAction>{{actionText}}</boldAction> - Grupa <boldName>{{name}}</boldName> jest <boldComparison>{{comparison}}</boldComparison>',
        groupDescriptionWithoutComparison:
          '<boldAction>{{actionText}}</boldAction> - Grupa <boldName>{{name}}</boldName> jest',
        groupIs: 'grupa jest',
        the: '- Grupa',
        title: 'Alerty',
      },
      totalWeightWarning: {
        message:
          'Ostrzeżenie! Waga całkowita ({{formattedTotalWeight}}kg) nie jest sumą wag w podziale ({{formattedSum}}kg).',
      },
    },
    cameraGuidance: {
      useCameraStream: {
        notifications: {
          cannotUseCamera: 'Nie można użyć aparatu',
          refreshAndAllowCamera:
            'Odśwież tę stronę i spróbuj ponownie. Zezwól na dostęp do aparatu, gdy pojawi się pytanie.',
        },
      },
    },
    common: {
      noData: 'Brak danych',
    },
    dealDetails: {
      fields: {
        destination: 'Miejsce docelowe',
        endProcessor: 'Przetwórca końcowy',
        inspections: 'Inspekcje',
        loadingSite: 'Miejsce załadunku',
        loads: 'Ładunki',
        material: 'Materiał',
        producer: 'Producent',
        risk: 'Ryzyko',
      },
      header: {
        deal: 'Transakcja {{dealId}}',
      },
    },
    dealRevenueDetails: {
      columns: {
        description: 'Opis',
        id: 'ID',
        invoiceStatus: 'Status faktury',
        invoiceXeroAmountPaid: 'Kwota zapłacona w Xero',
        invoiceXeroFullyPaidOn: 'W pełni opłacono w Xero dnia',
        invoiceXeroId: 'ID faktury Xero',
        lineTotal: 'Suma wiersza',
        quantity: 'Ilość',
        unitAmount: 'Cena jednostkowa',
      },
    },
    documentGeneration: {
      notifications: {
        failedToSaveFile: 'Nie udało się zapisać pliku',
        failedToUploadFile: 'Nie udało się przesłać pliku',
      },
    },
    evidence: {
      bulkDownload: {
        empty: 'Brak dowodów do pobrania',
        error: 'Nie udało się pobrać dowodów',
        inProgress: 'Przygotowywanie pobierania dowodów…',
        partialSuccess: 'Pobrano {{downloaded}} z {{total}} zdjęć',
        success: 'Pobrano {{count}} zdjęć',
      },
      common: {
        editSample: 'Edytuj próbkę',
        manageInspection: 'Zarządzaj inspekcją',
      },
      evidencePhotosViewer: {
        bulkDeleteError: 'Nie udało się usunąć zdjęć',
        bulkDeleteSuccess: 'Pomyślnie usunięto {{count}} zdjęć',
        noEvidenceFound: 'Nie znaleziono zdjęć',
      },
      loadingPhotoEvidenceGallery: {
        noOtherImagesAvailable: 'Brak innych dostępnych obrazów.',
      },
      manualBreakdownFormFields: {
        kilogramUnit: 'kg',
      },
      sessionSummary: {
        anonymous: 'Anonimowy',
        withOrigin: '{{origin}} - Przesłane przez {{uploadedBy}}',
        withoutOrigin: 'Przesłane przez {{uploadedBy}}',
      },
      uploadMenu: {
        downloadAllEvidence: 'Pobierz wszystkie dowody',
        downloadingAllEvidence: 'Pobieranie…',
        editEvidence: 'Edytuj dowody',
        menuAriaLabel: 'Więcej akcji',
        offlineModeComingSoon: 'Tryb offline już wkrótce',
        openCamera: 'Otwórz aparat',
        uploadEvidence: 'Prześlij dowody',
      },
      uploadOptionsButton: {
        actions: {
          upload: 'Prześlij',
        },
        options: {
          openCamera: 'Otwórz aparat',
          uploadPhoto: 'Prześlij zdjęcie',
        },
      },
    },
    evidenceGrid: {
      columns: {
        evidence: 'Dowód',
      },
    },
    externalLoad: {
      common: {
        supplier: 'Dostawca',
      },
    },
    findTicketsTable: {
      columns: {
        category: 'Kategoria',
        status: 'Status',
        summary: 'Podsumowanie',
        team: 'Zespół',
      },
    },
    firmQuoteBulkForm: {
      columns: {
        bunkerAdjustmentFactorCost: 'Koszt współczynnika korekty paliwowej (BAF)',
        bunkerAdjustmentFactorCurrency: 'Waluta współczynnika korekty paliwowej (BAF)',
        carrier: 'Przewoźnik',
        containerType: 'Typ kontenera',
        demandIncoterm: 'Incoterm popytu',
        destinationCountry: 'Kraj docelowy',
        destinationLat: 'Szer. geogr. miejsca docelowego',
        destinationLon: 'Dł. geogr. miejsca docelowego',
        destinationName: 'Nazwa miejsca docelowego',
        directLandCost: 'Bezpośredni koszt lądowy',
        directLandCurrency: 'Waluta bezpośredniego kosztu lądowego',
        e2eCost: 'Koszt E2E',
        e2eCurrency: 'Waluta E2E',
        estimatedDeparture: 'Szac. wypłynięcie',
        expiresOn: 'Wygasa',
        forwarder: 'Spedytor',
        notes: 'Notatki',
        oceanFreightCost: 'Koszt frachtu morskiego',
        oceanFreightCurrency: 'Waluta frachtu morskiego',
        onCarriageCost: 'Koszt dowozu (on-carriage)',
        onCarriageCurrency: 'Waluta dowozu (on-carriage)',
        originCountry: 'Kraj pochodzenia',
        originLat: 'Szer. geogr. miejsca pochodzenia',
        originLon: 'Dł. geogr. miejsca pochodzenia',
        originName: 'Nazwa miejsca pochodzenia',
        peakSeasonSurchargeCost: 'Koszt dopłaty za szczyt sezonu',
        peakSeasonSurchargeCurrency: 'Waluta dopłaty za szczyt sezonu',
        portOfDischarge: 'POD',
        portOfLoading: 'POL',
        preCarriageCost: 'Koszt dowozu wstępnego (pre-carriage)',
        preCarriageCurrency: 'Waluta dowozu wstępnego (pre-carriage)',
        quotedOn: 'Wyceniono dnia',
        supplyIncoterm: 'Incoterm podaży',
        typeOfGoods: 'Rodzaj towaru',
      },
    },
    grade: {
      compositionReport: {
        annotatedBaleAlt: 'Bela z adnotacjami',
      },
      imageQualityPrompt: {
        label: 'Zrób ponownie',
      },
    },
    groundControl: {
      common: {
        descriptions: {
          calculateDealProfitability: 'Oblicz rentowność transakcji',
          createPurchaseOrderAndSendToSeller: 'Utwórz zamówienie zakupu i wyślij je do sprzedawcy do podpisu',
          createSalesOrderAndSendToBuyer: 'Utwórz zamówienie sprzedaży i wyślij je do kupującego do podpisu',
          viewEditAndMatch: 'Przeglądaj, edytuj i dopasowuj',
        },
        labels: {
          createPurchaseOrder: 'Utwórz zamówienie zakupu',
          createSalesOrder: 'Utwórz zamówienie sprzedaży',
          dealProfitabilityCalculator: 'Kalkulator rentowności transakcji',
          viewPurchaseOrders: 'Pokaż zamówienia zakupu',
          viewSalesOrders: 'Pokaż zamówienia sprzedaży',
        },
      },
      tabs: {
        advanced: {
          label: 'Zaawansowane',
          links: {
            addExternalEvidence: {
              description: 'Dodaj dowody ze źródeł zewnętrznych',
              label: 'Dodaj zewnętrzne dowody',
            },
            manageMaterialProducts: {
              description: 'Przeglądaj i edytuj produkty materiałowe',
              label: 'Zarządzaj produktami materiałowymi',
            },
            pernType: {
              description: 'Aktualizuj typy PERN',
              label: 'Typ PERN',
            },
          },
        },
        contracts: {
          label: 'Umowy',
        },
        deals: {
          label: 'Transakcje',
          links: {
            goToDealHub: {
              description: 'Przeglądaj i realizuj zadania transakcji',
              label: 'Przejdź do DealHub',
            },
            viewAllDeals: {
              description: 'Przeglądaj i edytuj wszystkie transakcje i ładunki',
              label: 'Pokaż wszystkie transakcje',
            },
          },
        },
        finance: {
          label: 'Finanse',
          links: {
            createShippingRate: {
              description: 'Utwórz stawkę transportową i wszystkie powiązane szczegóły',
              label: 'Utwórz stawkę transportową',
            },
            manageBanks: {
              description: 'Przeglądaj i edytuj dane bankowe',
              label: 'Zarządzaj bankami',
            },
            manageCosts: {
              description: 'Twórz, edytuj i przeglądaj koszty',
              label: 'Zarządzaj kosztami',
            },
            manageExternalCompanies: {
              description: 'Twórz, edytuj i przeglądaj dane firm zewnętrznych',
              label: 'Zarządzaj firmami zewnętrznymi',
            },
            manageFinancing: {
              description: 'Wysyłaj żądania płatności do Finmid',
              label: 'Zarządzaj finansowaniem',
            },
            manageRevenue: {
              description: 'Twórz, edytuj i przeglądaj przychody',
              label: 'Zarządzaj przychodami',
            },
            marketPernPrices: {
              description: 'Przeglądaj historyczne ceny PERN z T2E',
              label: 'Rynkowe ceny PERN',
            },
          },
        },
        support: {
          label: 'Wsparcie',
          links: {
            getSupport: {
              description: 'Utwórz zgłoszenie wsparcia w Jira',
              label: 'Uzyskaj wsparcie',
            },
            helpAndLearn: {
              description: 'Przewodniki pomocy',
              label: 'Pomoc i nauka',
            },
          },
        },
        trade: {
          label: 'Handel',
          links: {
            generateDocuments: {
              description: 'Generuj dokumenty dla zamówień zakupu, zamówień sprzedaży, wysyłek itp.',
              label: 'Generuj dokumenty',
            },
            transportMetabase: {
              description: 'Link do Transport Metabase',
              label: 'Transport Metabase',
            },
          },
        },
        usersAndCompanies: {
          label: 'Użytkownicy i firmy',
          links: {
            copySessions: {
              description: 'Kopiuj sesje do ML Accuracy Testing z pliku CSV',
              label: 'Kopiuj sesje',
            },
            createUserAndCompany: {
              description: 'Utwórz użytkownika i nową firmę lub zaproś do istniejącej firmy',
              label: 'Utwórz użytkownika i firmę',
            },
            hijackUser: {
              description: 'Szybko przejmij konto użytkownika',
              label: 'Przejmij użytkownika',
            },
            staffOnboarding: {
              description: 'Utwórz nowego użytkownika administratora i dodaj go do Safi',
              label: 'Onboarding pracownika',
            },
            viewTradeCompanies: {
              description: 'Przeglądaj i edytuj firmy handlowe po stronie podaży i popytu',
              label: 'Pokaż wszystkie firmy handlowe',
            },
          },
        },
      },
    },
    guidePriceChip: {
      label: 'Cena orientacyjna',
      unavailable: 'Cena orientacyjna niedostępna',
    },
    helpGuides: {
      aiBale: {
        partOne: {
          boldText: 'Stań maks. 1 metr',
          imageAlt: 'Stań maksymalnie 1 metr od stosu',
          trailingText: 'przed stosem.',
        },
      },
      aiLoose: {
        partFour: {
          and: 'i',
          clearPile: 'Uprzątnij stos',
          imageAlt: 'Uprzątnij stos',
          repeatSteps: 'powtórz kroki 1-3',
          untilText: 'aż zrobisz po jednym zdjęciu każdego stosu dla całej próbki.',
        },
        partOne: {
          imageAlt: 'Zrób stos',
          makePile: 'Zrób stos',
          middleText: 'z próbki i rozłóż go równomiernie na podłodze.',
          takePhoto: 'Zrób 1 zdjęcie na stos.',
        },
        partThree: {
          boldIntro: 'Sprawdź zdjęcie przed uprzątnięciem stosu.',
          imageAlt: 'Przejrzyj i usuń',
          trailingText: 'Jeśli nie jest poprawne, możesz je usunąć i zrobić nowe.',
          warningBold: 'Tylko 1 zdjęcie na stos',
          warningPrefix: 'Prześlij',
          warningSuffix: 'aby nie zaburzyć wyników AI.',
        },
      },
      common: {
        completeAlt: 'Ukończono',
      },
      data: {
        guides: {
          aiBale: 'AI Bela',
          aiBriquette: 'AI Brykiet',
          aiLoose: 'AI Luzem',
          emailSupport: 'Napisz do nas na support@safi.co',
        },
        sections: {
          contactSupport: 'Skontaktuj się ze wsparciem',
          qualityControlGuides: 'Przewodniki kontroli jakości',
        },
        subcategories: {
          emailSupportPrompt: 'Potrzebujesz wsparcia lub chcesz zgłosić nową funkcję?',
          howToTakeGreatAiPhotos: 'Jak robić świetne zdjęcia dla AI',
        },
      },
    },
    manageDataButton: {
      buttonLabel: 'Zarządzaj danymi',
      options: {
        aiBalePhotos: 'Zdjęcia beli AI',
        loadingEvidence: 'Dowody załadunku',
        marketingMedia: 'Media marketingowe',
        samples: 'Próbki',
        siteEvidence: 'Dowody obiektu',
        specifications: 'Specyfikacje',
      },
    },
    materialCategoryActions: {
      notifications: {
        cannotSaveMaterial: 'Nie można zapisać materiału',
        errorCreatingMaterial: 'Błąd podczas tworzenia materiału',
        errorDeletingMaterial: 'Błąd podczas usuwania materiału',
        errorSavingMaterial: 'Błąd podczas zapisywania materiału',
        savedSuccessfully: 'Zapisano pomyślnie',
        selectedTargetGroupHasNoOperands: 'Wybrana grupa docelowa nie ma operandów. Wybierz inną grupę docelową.',
        skippedSpecifications: 'Nie zapisano {{count}} specyfikacji, ponieważ nie mają przypisanych operandów.',
      },
    },
    materialComponents: {
      supportedComponentList: {
        buttonText: 'Dodaj komponent materiału',
        title: 'Komponenty materiału',
      },
    },
    materialDirtLevel: {
      options: {
        clean: 'Czysty',
        dirty: 'Brudny',
        notAvailable: 'Nd.',
        veryClean: 'Bardzo czysty',
      },
    },
    materialProducts: {
      validation: {
        atLeastOneBuyerMaterialComponentIsRequired: 'Wymagany jest co najmniej jeden komponent materiału kupującego.',
        atLeastOneSellerMaterialComponentIsRequired: 'Wymagany jest co najmniej jeden komponent materiału sprzedawcy.',
        buyerAndSellerMaterialComponentCountsMustMatch:
          'Liczba wartości komponentów materiału kupującego i sprzedawcy musi być taka sama.',
      },
    },
    materialProductsList: {
      columns: {
        buyerMaterialComponents: 'Komponenty materiału kupującego',
        capsAndLabels: 'Nakrętki i etykiety',
        country: 'Kraj',
        foodGrade: 'Klasa spożywcza',
        id: 'ID',
        materialGrade: 'Klasa materiału',
        materialOrigin: 'Pochodzenie materiału',
        materialSpec: 'Specyfikacja materiału',
        process: 'Proces',
        sellerMaterialComponents: 'Komponenty materiału sprzedawcy',
      },
      values: {
        no: 'Nie',
        notSpecified: 'Nd.',
        yes: 'Tak',
      },
    },
    notificationParty: {
      label: 'Strona do powiadamiania',
      options: {
        allViaAgent: 'Cała komunikacja przez agenta',
        directWithBuyer: 'Komunikacja bezpośrednio z kupującym',
      },
    },
    orders: {
      validation: {
        fixedPriceAndDateMustBeProvidedTogether: 'Cena stała i data stała muszą być podane razem',
      },
    },
    paymentMethod: {
      label: 'Metoda płatności',
      options: {
        documentAgainstPayment: 'Dokumenty za zapłatę',
        telegraphicTransfer: 'Przelew telegraficzny',
      },
    },
    percentageLightBlue: {
      label: 'Procent jasnoniebieskiego',
      options: {
        notAvailable: 'Nd.',
      },
    },
    siteMaterials: {
      notifications: {
        errorCreatingMaterial: 'Błąd podczas tworzenia materiału',
        materialCreated: 'Materiał utworzony',
        productLinkedSuccessfully: 'Produkt pomyślnie powiązany',
      },
    },
    timeFilter: {
      options: {
        last30Days: 'Ostatnie 30 dni',
        last7Days: 'Ostatnie 7 dni',
        last90Days: 'Ostatnie 90 dni',
        next14Days: 'Następne 14 dni',
        next2Days: 'Następne 2 dni',
        next7Days: 'Następne 7 dni',
      },
    },
    variablePricingFields: {
      indexOptions: {
        lmeAluminium: 'LME Aluminium',
        lmeAluminiumMidwestPremium: 'LME Aluminium + Midwest Premium',
      },
    },
  },
  breakdowns: {
    adminAiVersionTitle: {
      value: 'AI:{{version}}',
    },
    aiSampleBreakdown: {
      analysisNotAvailable: 'Analiza niedostępna',
    },
    aluMaterialSampleBreakdown: {
      labels: {
        aluminium: 'Aluminium',
        foilAndTray: 'Folia i tacka',
        foodCan: 'Puszka spożywcza',
        nonAluminium: 'Niealuminiowe',
        nonMetal: 'Niemetalowe',
        otherAluminium: 'Inne aluminium',
        otherMetal: 'Inny metal',
        total: 'Razem',
        ubc: 'UBC',
      },
    },
    analysisInProgressAlert: {
      partialDescription:
        'Poniższe wyniki opierają się na {{acceptedCount}} z {{submittedCount}} zdjęć. {{pendingCount}} kolejnych jest wciąż analizowanych.',
      partialTitle: 'Analizowanych jest więcej zdjęć',
      pendingDescription:
        '{{pendingCount}} z {{submittedCount}} zdjęć jest wciąż analizowanych. Wyniki powinny pojawić się w ciągu kilku minut.',
      pendingTitle: 'Analiza w toku',
      photoPendingDescription:
        'Twoje zdjęcie jest wciąż analizowane w tle. Wyniki i kontury powinny pojawić się w ciągu kilku minut.',
      rejectedDescription: 'Odrzucono {{rejectedCount}} zdjęć. Sprawdź powód przy każdym zdjęciu.',
    },
    cleanliness: {
      label: 'Czystość',
      tooltip:
        'Czystość to miara tego, jak czysty jest materiał. Skala obejmuje: bardzo czysty, czysty, średni i brudny. Zabrudzenia obejmują tłuszcz, plamy i kurz na powierzchni materiału.',
    },
    evaluatedSpecsBreakdown: {
      customizeTableFor: 'Dostosuj tabelę dla',
      targetChip: 'Docelowy',
      thisMaterial: 'ten materiał',
      title: 'Analiza AI',
    },
    noAnalysisAlert: {
      description:
        'AI nie mogła przeanalizować zdjęć. Możesz sprawdzić powód przy każdym zdjęciu, usunąć odrzucone i spróbować ponownie.',
      guidance:
        'Zapoznaj się z <guideLink>przewodnikiem {{guideLabel}}</guideLink>, aby uzyskać wskazówki dotyczące robienia lepszych zdjęć.',
      noSubmittedPhotosDescription:
        'Nie przesłano jeszcze żadnych zdjęć do analizy. Dodaj zdjęcia, aby rozpocząć analizę.',
      title: 'Brak dostępnych wyników',
    },
    petFlake: {
      fields: {
        bulkDensity: 'Gęstość nasypowa',
        clearFlakes: 'Przezroczyste płatki',
        colouredFlakes: 'Kolorowe płatki',
        darkBlueFlakes: 'Ciemnoniebieskie płatki',
        ferrousMetal: 'Metal żelazny',
        glue: 'Klej',
        greenFlakes: 'Zielone płatki',
        intrinsicViscosity: 'Lepkość istotna',
        isophthalicAcid: 'Kwas izoftalowy',
        lightBlueFlakes: 'Jasnoniebieskie płatki',
        meltFlowRate: 'Wskaźnik płynięcia stopu (MF) (260°C/2,16Kg)',
        meltingPoint: 'Temperatura topnienia',
        nonFerrousMetal: 'Metal nieżelazny',
        otherImpurities: 'Inne zanieczyszczenia',
        paper: 'Papier',
        particlesSizeBetween10And12mm: 'Cząstki o wielkości od 10 do 12 mm',
        particlesSizeBetween5And10mm: 'Cząstki o wielkości od 5 do 10 mm',
        particlesSizeLt5mm: 'Cząstki o wielkości < 5 mm',
        petContent: 'Zawartość politereftalanu etylenu (PET)',
        ph: 'pH',
        polyolefinContamination: 'Zanieczyszczenie poliolefinami',
        pvcContamination: 'Zanieczyszczenie PVC',
        residualContamination: 'Zanieczyszczenie resztkowe',
        residualMoisture: 'Wilgotność resztkowa',
        sampledBy: 'Próbkę pobrał',
        woodenMaterials: 'Materiały drewniane',
        yellowFlakes: 'Żółte płatki',
      },
    },
    petMaterialSampleBreakdown: {
      labels: {
        clear: 'Przezroczysty',
        clearFullSleeveNonFoodGrade: 'Przezroczysty pełny sleeve, niespożywczy',
        clearNonFoodGrade: 'Przezroczysty, niespożywczy',
        clearPetTray: 'Przezroczysta tacka PET',
        darkBlue: 'Ciemnoniebieski',
        film: 'Folia',
        green: 'Zielony',
        largeClear5L: 'Duży przezroczysty (≥5L)',
        lightBlue: 'Jasnoniebieski',
        liquid: 'Płyn',
        nonPetBottle: 'Butelka nie-PET',
        nonPlastic: 'Nieplastikowe',
        other: 'Inne',
        otherPlastic: 'Inny plastik',
        petBottle: 'Butelka PET',
        prohibitive: 'Niedozwolone',
        total: 'Razem',
      },
    },
    taintTaborMaterialSampleBreakdown: {
      labels: {
        aluIron: 'Alu./żelazo',
        aluNonMetal: 'Alu./niemetal',
        aluWithAttachments: 'Alu. z elementami dodatkowymi',
        aluminium: 'Aluminium',
        cast: 'Odlew',
        castIron: 'Odlew/żelazo',
        ferrousMetal: 'Metal żelazny',
        finesLt20mm: 'Drobne frakcje <20mm',
        nonAluminium: 'Niealuminiowe',
        nonMetal: 'Niemetalowe',
        otherMetal: 'Inny metal',
        prohibitive: 'Niedozwolone',
        radiatorCondenser: 'Chłodnica/skraplacz',
        stainlessSteel: 'Stal nierdzewna',
        taintTabor: 'Taint Tabor',
        total: 'Razem',
        ubcFoil: 'UBC/folia',
      },
    },
    zorbaMaterialSampleBreakdown: {
      labels: {
        aluminiumUbc: 'Aluminium - UBC',
        compositeMaterials: 'Materiały kompozytowe',
        copperBrassRedsYellows: "Miedź i mosiądz - 'czerwone i żółte'",
        finesLt20mm: 'Drobne frakcje <20mm',
        nonFerrousMetalWhites: "Metal nieżelazny - 'białe'",
        nonTarget: 'Niedocelowy',
        otherMetalFerrous: 'Inny metal (żelazny)',
        otherNonMetal: 'Inny niemetal',
        postConsumerAluminiumPackaging: 'Poużytkowe opakowania aluminiowe',
        prohibitive: 'Niedozwolone',
        targetMaterial: 'Materiał docelowy',
        total: 'Razem',
      },
    },
  },
  cameraGuidance: {
    camera: {
      notifications: {
        autoCaptureOff: 'Automatyczne robienie zdjęć wyłączone',
        autoCaptureOn: 'Automatyczne robienie zdjęć włączone',
      },
      rejectedPhotos: {
        message: 'Dotknij zdjęcia poniżej, aby zobaczyć powód błędu, a następnie usuń zdjęcie i spróbuj ponownie',
        photo: 'zdjęcie',
        photos: 'zdjęć',
        title: '{{prefix}} sprawdzić {{count}} {{countLabel}}!',
        titlePrefixObfuscated: 'Nie udało się',
        titlePrefixWithAi: 'AI nie mogła',
      },
    },
    cameraFooter: {
      actions: {
        capture: 'Zrób zdjęcie',
        complete: 'Zakończ',
        guide: 'Przewodnik',
      },
      tooltips: {
        takePhotoToComplete: 'Zrób zdjęcie, aby zakończyć',
      },
    },
    cameraHeader: {
      autoCapture: 'Automatyczne robienie zdjęć',
      confirmationDialog: {
        actions: {
          complete: 'Zakończ',
          exitAnyway: 'Wyjdź mimo to',
        },
        messageWithAiLabel:
          'Twoje zdjęcia zostaną zapisane, ale nie zobaczysz od razu wyników AI. Aby zobaczyć raport AI teraz, dotknij Zakończ.',
        messageWithoutAiLabel:
          'Twoje zdjęcia zostaną zapisane, ale nie zobaczysz od razu wyników. Aby zobaczyć raport teraz, dotknij Zakończ.',
        title: 'Wyjść z aparatu?',
      },
      flashAriaLabel: 'Przełącz lampę błyskową',
      settings: {
        description: 'Użyj trybu offline, jeśli masz słabe połączenie danych, aby nie utracić zdjęć',
        title: 'Ustawienia',
      },
    },
    countdownOverlay: {
      snap: 'Pstryk!',
    },
    previewImageReview: {
      actions: {
        deleteAndRetake: 'Usuń i zrób ponownie',
      },
      evidencePhotoAlt: 'Zdjęcie dowodowe',
      notifications: {
        deleteFailed: 'Nie udało się usunąć tego zdjęcia. Spróbuj ponownie.',
      },
    },
  },
  charts: {
    targetBaleWeightsHistogram: {
      axes: {
        x: 'Przewidywany % zawartości docelowej',
        y: 'Liczba beli',
      },
      labels: {
        average: 'Średnia: {{targetGrade}}%',
        bales: 'Bele',
      },
      tooltip: {
        label: 'Wykryty %: {{label}}',
        value: '{{value}} beli',
      },
    },
  },
  dataCards: {
    aluminiumComposition: {
      reportTitle: 'Raport',
      targetDistributionTitle: 'Rozkład docelowy',
    },
    common: {
      loads: 'Ładunki',
      metricTonnes: 'Tony metryczne',
    },
    hdpeComposition: {
      aiBaleAnalysisTitle: 'Analiza beli AI',
      reportTitle: 'Raport',
    },
    loadingDetails: {
      actions: {
        showMedia: 'Pokaż media',
      },
      fields: {
        loadingNotes: 'Notatki załadunkowe',
        loadingTechnique: 'Technika załadunku',
        packedOn: 'Zapakowano dnia',
        targetPayload: 'Docelowy ładunek',
      },
      title: 'Szczegóły załadunku',
      values: {
        targetPayloadMt: '{{targetPayload}} (MT)',
      },
    },
    materialNotAnalysed: {
      analysisTitle: 'Analiza',
      noResultsDescription:
        'Nie zbudowaliśmy jeszcze modelu dla tego konkretnego typu materiału. Jednak dzięki większej ilości danych będziemy mogli w przyszłości udostępnić analizę.',
      noResultsTitle: 'Brak dostępnych wyników',
      reportTitle: 'Raport',
    },
    materialSpecifications: {
      actions: {
        showMedia: 'Pokaż media',
      },
      analysis: {
        completed: 'Ukończono',
        notCompleted: 'Nie ukończono',
      },
      fields: {
        baleDimensions: 'Wymiary beli',
        capsAndLabels: 'Nakrętki i etykiety',
        density: 'Gęstość (kg/m³)',
        ferrousCheck: 'Kontrola metali żelaznych',
        foodGrade: 'Klasa spożywcza',
        materialAnalysisNotes: 'Notatki z analizy materiału',
        materialForm: 'Postać materiału',
        materialProcess: 'Proces materiału',
        origin: 'Pochodzenie',
        radiationCheck: 'Kontrola promieniowania',
        strapping: 'Bandowanie',
        visualCheck: 'Kontrola wizualna',
      },
      title: 'Specyfikacje materiału',
      units: {
        meters: 'm',
      },
      values: {
        no: 'Nie',
        yes: 'Tak',
      },
    },
    petComposition: {
      aiBaleAnalysisTitle: 'Analiza beli AI',
      reportTitle: 'Raport',
    },
    site: {
      actions: {
        showMedia: 'Pokaż media',
      },
      fields: {
        machinesUsed: 'Używane maszyny',
        materialOrigin: 'Pochodzenie materiału',
        monthlyVolume: 'Wolumen miesięczny',
        siteGround: 'Podłoże obiektu',
        siteNotes: 'Notatki o obiekcie',
        storageAndCover: 'Magazynowanie i zadaszenie',
      },
      monthlyVolumeValue: '{{productionVolume}} (MT)',
      title: 'Obiekt',
    },
    tradeCompanySessionLoose: {
      aiLooseAnalysisTitle: 'Analiza AI luzem',
      reportTitle: 'Raport',
      sampleTotalWeightTitle: 'Waga całkowita próbki',
    },
    transactionHistory: {
      alerts: {
        newSupplier: 'Nowy dostawca! Nie kupowałeś jeszcze od niego wcześniej.',
        purchasedBefore: 'Kupowałeś już wcześniej od tego dostawcy!',
      },
      title: 'Historia transakcji',
      total: {
        description: 'Transakcje między tym dostawcą a innymi kupującymi w ciągu ostatnich 6 miesięcy.',
      },
    },
  },
  error: {
    actions: {
      contactSupport: 'Skontaktuj się ze wsparciem',
      goToHomepage: 'Przejdź do strony głównej',
      tryAgain: 'Spróbuj ponownie',
    },
    forbidden: {
      description: 'Nie masz uprawnień do wyświetlenia tej strony.',
      title: 'Odmowa dostępu',
    },
    generic: {
      description: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
      title: 'Coś poszło nie tak',
    },
    network: {
      description: 'Sprawdź połączenie internetowe i spróbuj ponownie.',
      title: 'Wygląda na to, że jesteś offline',
    },
    notFound: {
      description: 'Nie mogliśmy znaleźć strony, której szukasz.',
      title: 'Nie znaleziono strony',
    },
    server: {
      description: 'Mamy problem z połączeniem z naszymi serwerami. Spróbuj ponownie za chwilę.',
      title: 'Coś poszło nie tak',
    },
    support: {
      body: 'Napotkałem błąd i potrzebuję pomocy.\n\nStrona: {{url}}\nSzczegóły: {{detail}}\n\nCo robiłem:',
      subject: 'Safi Quality — Potrzebuję pomocy z błędem',
    },
  },
  evidence: {
    actionBar: {
      deletePrompt: 'Czy na pewno chcesz usunąć wybrane dowody?',
      deleteTitle: 'Usuń {{evidence}}',
      selectedCount: 'Wybrano: {{selected}}',
    },
    chips: {
      ai: 'AI',
      selfServe: 'Samoobsługa',
    },
    defaultBreakdownFormFields: {
      actions: {
        addBreakdownItem: 'Dodaj pozycję podziału',
      },
      labels: {
        name: 'Nazwa',
        unit: 'Jednostka',
        value: 'Wartość',
      },
      title: 'Podział',
    },
    editForm: {
      actions: {
        delete: 'Usuń',
        edit: 'Edytuj',
      },
      labels: {
        newEvidenceType: 'Nowy typ {{evidence}}',
        newLabel: 'Nowy',
        typeLabel: 'Typ',
      },
      notifications: {
        deleteError: 'Błąd podczas usuwania dowodu',
        deleteSuccess: 'Dowód usunięty',
        updateError: 'Błąd podczas aktualizacji dowodu',
        updateSuccess: 'Dowód zaktualizowany',
      },
      validation: {
        typeOrSiteMaterialRequired: 'Należy podać typ dowodu lub materiał obiektu',
      },
      warnings: {
        deleteMessage:
          'Zamierzasz usunąć ten dowód z tego materiału obiektu. Spowoduje to jego usunięcie z powiązanego raportu jakości. Potwierdź, aby kontynuować.',
        editMessage:
          'Zamierzasz zmienić ten dowód. Spowoduje to jego usunięcie z powiązanego raportu jakości. Potwierdź, aby kontynuować.',
        title: 'Ostrzeżenie!',
      },
    },
    inspectionSessionDetails: {
      labels: {
        dealId: 'ID transakcji:',
        inspectionSlug: 'Slug inspekcji:',
      },
      tooltips: {
        reportAvailableOnCompletion: 'Po zakończeniu inspekcji znajdziesz tutaj link do raportu.',
        searchInDrive: 'Wyszukaj inspekcję w Google Drive',
        viewDealDetails: 'Zobacz szczegóły transakcji',
        viewExternalReport: 'Zobacz raport zewnętrzny',
      },
    },
    offlineGallery: {
      notifications: {
        cachedClearError: 'Nie udało się wyczyścić zapisanych zdjęć',
        cachedClearSuccess: 'Zapisane zdjęcia wyczyszczone',
        cachedSyncError: 'Nie udało się zsynchronizować zapisanych zdjęć',
        cachedSyncSuccess: 'Zdjęcia przesłane. Analiza może potrwać kilka minut.',
        clearError: 'Nie udało się usunąć dowodów offline',
        clearSuccess: 'Pomyślnie usunięto dowody offline',
        missingParentViewCache:
          'Jedno lub więcej zapisanych zdjęć należy do raportu, który już nie istnieje. Otwórz pamięć podręczną, aby je pobrać i przesłać ponownie, lub je usuń.',
      },
      title: 'Offline {{evidenceLower}}',
      tooltips: {
        cannotSyncWithoutInternet: 'Nie można synchronizować bez połączenia z internetem',
        clear: 'Wyczyść dowody offline',
        sync: 'Synchronizuj dowody offline',
        syncInProgress: 'Synchronizacja w toku...',
      },
    },
    sampleBreakdown: {
      labels: {
        aiBreakdown: 'Podział AI',
        manualBreakdown: 'Podział ręczny',
        noNotes: 'Brak notatek',
        sample: 'Próbka',
        sampleEvidence: 'Dowody próbki',
      },
      totalWeight: '{{totalWeight}}kg',
    },
    sampleFormFields: {
      breakdowns: {
        alu: {
          groups: {
            aluminium: 'Aluminium',
            nonAluminium: 'Niealuminiowe',
          },
          items: {
            foilAndTray: 'Folia i tacka',
            foodCan: 'Puszka spożywcza',
            nonMetal: 'Niemetalowe',
            otherAluminium: 'Inne aluminium',
            otherAluminiumAerosols: 'Inne aluminium (aerozole)',
            otherMetal: 'Inny metal',
            rigidPlastics: 'Sztywne tworzywa',
            ubc: 'UBC',
            vapes: 'E-papierosy',
          },
        },
        pet: {
          groups: {
            otherPetAndPlastics: 'Inne PET i tworzywa',
            petBottles: 'Butelki PET',
          },
          items: {
            clear: 'Przezroczysty',
            clearFullSleeve: 'Przezroczysty (pełny sleeve)',
            clearLarge: 'Przezroczysty (duży)',
            clearPetTray: 'Przezroczysta tacka PET',
            darkBlue: 'Ciemnoniebieski',
            film: 'Folia',
            green: 'Zielony',
            lightBlue: 'Jasnoniebieski',
            liquid: 'Płyn',
            nonFoodGradeBottle: 'Butelka PET niespożywcza',
            nonPlastic: 'Nieplastikowe',
            other: 'Inne',
            otherPlastic: 'Inny plastik',
            prohibitive: 'Niedozwolone',
          },
        },
        taintTabor: {
          groups: {
            aluWithAttachments: 'Alu. z elementami dodatkowymi',
            aluminium: 'Aluminium',
            nonAluminium: 'Niealuminiowe',
          },
          items: {
            aluIron: 'Alu./żelazo',
            aluNonMetal: 'Alu./niemetal',
            cast: 'Odlew',
            castIron: 'Odlew/żelazo',
            ferrousMetal: 'Metal żelazny',
            finesLt20mm: 'Drobne frakcje <20mm',
            nonMetal: 'Niemetalowe',
            otherMetal: 'Inny metal',
            prohibitive: 'Niedozwolone',
            radiatorCondenser: 'Chłodnica/skraplacz',
            stainlessSteel: 'Stal nierdzewna',
            taintTabor: 'Taint Tabor',
            ubcFoil: 'UBC/folia',
          },
        },
        zorba: {
          groups: {
            nonTarget: 'Niedocelowy',
            targetMaterial: 'Materiał docelowy',
          },
          items: {
            aluminiumFoilUbc: 'Aluminium - folia / UBC',
            compositeMaterials: 'Materiały kompozytowe',
            copperAndBrass: "Miedź i mosiądz 'czerwone i żółte'",
            finesLt20mm: 'Drobne frakcje <20mm',
            nonFerrousMetal: "Metal nieżelazny 'białe'",
            otherMetalFerrous: 'Inny metal (żelazny)',
            otherNonMetal: 'Inny niemetal',
            postConsumerAluminiumPackaging: 'Poużytkowe opakowania aluminiowe',
            prohibitive: 'Niedozwolone',
          },
        },
      },
      labels: {
        notes: 'Notatki',
        totalWeight: 'Waga całkowita',
        weightUnit: 'kg',
      },
    },
    sampleMaterialEditForm: {
      actions: {
        edit: 'Edytuj',
      },
      warning: {
        editMessage:
          'Zamierzasz zmienić ten dowód. Spowoduje to jego usunięcie z powiązanego raportu jakości. Potwierdź, aby kontynuować.',
        title: 'Ostrzeżenie!',
      },
    },
    sampleSessionDetails: {
      manualSampleAnalysisTitle: 'Analiza ręczna próbki',
      noData: 'Brak danych',
      sampleImagesTitle: 'Zdjęcia próbki',
    },
    sessionStats: {
      excludedSubtitle: 'Obrazy nie spełniły naszych reguł wymagań',
      excludedTitle: 'Wykluczone',
      includedSubtitle: 'Obrazy spełniły nasze reguły wymagań',
      includedTitle: 'Uwzględnione',
    },
    unloadingPhotoUploader: {
      label: 'Prześlij zdjęcia',
      notifications: {
        uploadFailed: 'Przesyłanie nie powiodło się',
        uploadSuccess: 'Przesyłanie zakończone powodzeniem',
      },
    },
  },
  helpGuides: {
    aiBale: {
      partTwo: {
        captions: {
          clearWhichBaleToFocusOn: 'Jasne, na której beli się skupić',
          goodQualityPhoto: 'Zdjęcie dobrej jakości',
          goodQualityPhotoAndDistanceFromBale: 'Zdjęcie dobrej jakości i dobra odległość od beli',
          greatDistanceFromBale: 'Świetna odległość od beli',
          materialNotTooTightlyPacked: 'Materiał nie jest zbyt ciasno upakowany',
          materialTooFlatAndTightlyPacked: 'Materiał jest zbyt płaski i zbyt ciasno upakowany',
          nearlyAllBaleFaceShown: 'Widoczna jest prawie cała powierzchnia beli',
          photoTooBlurry: 'Zdjęcie jest zbyt rozmyte',
          someBackgroundIsOk: 'Trochę tła jest w porządku',
          tooClose: 'Zbyt blisko',
          tooManyBalesAngleNotStraightPoorQuality: 'Zbyt wiele beli, kąt nie jest frontalny, słaba jakość',
          tooManyBalesManySidesVisible: 'Zbyt wiele beli, widocznych wiele stron',
          tooManyBalesTooFarAway: 'Zbyt wiele beli, zbyt daleko',
          unclearWhichIsMainBale: 'Niejasne, która bela jest główna',
        },
        intro: 'Oto przykłady dobrych i złych zdjęć:',
      },
    },
    aiBriquette: {
      partOne: {
        imageAlt: 'Stań maksymalnie 0,5 metra od',
        instructions: '<bold>Stań 0,5 metra</bold> przed brykietem.',
      },
      partTwo: {
        analysisInstruction: 'Podczas analizy palety <bold>zrób zdjęcia min. 6 brykietów.</bold>',
        captions: {
          goodDistance: 'Dobra odległość',
          markingsVisible: 'Oznaczenia widoczne',
          noMarkingsVisible: 'Brak widocznych oznaczeń',
          okDistance: 'Odległość OK',
          tooDirty: 'Zbyt brudny',
          tooShredded: 'Zbyt rozdrobniony',
        },
        intro: 'Oto przykłady dobrych i złych zdjęć:',
      },
    },
    aiLoose: {
      partTwo: {
        captions: {
          cannotSeeAllMaterial: 'Nie widać całego materiału',
          floorHasLargeMarkings: 'Podłoga ma duże oznaczenia',
          materialInsideBlackBox: 'Materiał znajduje się wewnątrz czarnej ramki',
          materialNotOnGroundOrTable: 'Materiał nie leży na podłodze/stole',
          materialShouldNotTouchPurpleLines: 'Materiał nie powinien dotykać fioletowych linii',
          materialSpreadVeryWell: 'Materiał jest bardzo dobrze rozłożony',
          materialSpreadWell: 'Materiał jest dobrze rozłożony',
          materialTouchesEdge: 'Materiał dotyka krawędzi',
          shadow: 'Cień',
          squareFullOfMaterial: 'Kwadrat jest pełen materiału',
          tooHeapedAndTooBig: 'Zbyt usypany i zbyt duży',
        },
        intro: 'Oto przykłady dobrych i złych zdjęć:',
      },
    },
    alert: {
      actions: {
        startGuide: 'Rozpocznij przewodnik - 1 min',
      },
      description: {
        obfuscatedAi: 'Przeczytaj ten krótki przewodnik, aby robić właściwe zdjęcia.',
        withAi:
          'Przeczytaj ten krótki przewodnik, aby robić właściwe zdjęcia dla dokładnych wyników AI. Złe zdjęcia = złe wyniki.',
      },
      title: 'Pierwszy raz używasz {{modelTitle}}?',
    },
    autoCaptureTip: {
      message:
        'Wskazówka! Pracuj szybciej i nie zdejmuj rękawic dzięki funkcji <autoCapture>Automatyczne robienie zdjęć</autoCapture>, która automatycznie robi zdjęcia za Ciebie.',
    },
    common: {
      actions: {
        back: 'Wstecz',
        finish: 'Zakończ',
        next: 'Dalej',
        skipGuide: 'Pomiń przewodnik',
      },
      models: {
        bale: 'bela',
        briquette: 'brykiet',
      },
    },
    complete: {
      message:
        'Gdy skończysz, dotknij <complete>„Zakończ”</complete> w aparacie. Następnie zobaczysz <qualityReport>Raport jakości.</qualityReport>',
    },
    dialog: {
      stepLabel: 'Krok {{currentStep}}/{{maxSteps}}',
      title: 'Witamy w {{modelTitle}}!',
    },
    guideTitle: {
      obfuscatedAi: 'Dowiedz się, jak prawidłowo robić zdjęcia, aby uzyskać najlepsze wyniki.',
      withAi: 'Dowiedz się, jak prawidłowo robić zdjęcia, aby uzyskać najlepsze wyniki AI.',
    },
    offlineWarning: {
      message:
        'Wskazówka! Włącz <offlineMode>Tryb offline</offlineMode>, aby nie utracić zdjęć, gdy Twoje połączenie danych jest słabe.',
    },
    reviewDeleteHelpText: {
      withAi:
        'Możesz sprawdzić, czy {{model}} został <accepted>zaakceptowany</accepted> czy <rejected>odrzucony</rejected> przez AI. <delete>Usuń</delete> i <retake>zrób ponownie</retake> nieprawidłowe zdjęcia.',
      withoutAi:
        'Możesz sprawdzić, czy {{model}} został <accepted>zaakceptowany</accepted> czy <rejected>odrzucony</rejected>. <delete>Usuń</delete> i <retake>zrób ponownie</retake> nieprawidłowe zdjęcia.',
    },
    reviewDeleteTip: {
      message:
        'Wskazówka! W Raporcie jakości możesz też <reviewDelete>przeglądać, usuwać</reviewDelete> oraz <addMorePhotos>dodawać więcej zdjęć</addMorePhotos>.',
    },
  },
  instructions: {
    photoInstructionsDrawer: {
      actions: {
        doNotShowAgain: 'Nie pokazuj ponownie',
        takePhoto: 'Zrób zdjęcie',
      },
      description:
        'Nasza AI analizuje przesłane obrazy według wielu parametrów. Twoje przesłane obrazy powinny wyglądać jak poniżej.',
      frontFaceDescription:
        'Najczęstszą przyczyną nieprawidłowego obrazu jest orientacja beli. Obiekty w beli powinny być spłaszczone i skierowane frontalnie.',
      frontFaceTitle: 'Czym jest strona frontowa?',
      imageAlt: {
        balePhotoExample: 'Przykład zdjęcia beli',
        frontFaceBalePhoto: 'Zdjęcie beli od strony frontowej',
        treeRingBalePhoto: 'Zdjęcie beli w układzie słojów',
      },
      labels: {
        objectsFlattenedFaceOn: 'Obiekty spłaszczone i frontalne',
        objectsSquashedSideOn: 'Obiekty zgniecione i boczne',
      },
      title: 'Robienie zdjęć',
    },
  },
  materialComponents: {
    supportedComponent: {
      labels: {
        materialComponent: 'Komponent materiału',
      },
    },
    supportedComponentValue: {
      adornments: {
        max: 'Maks.: ',
        min: 'Min: ',
      },
      labels: {
        booleanValue: 'Wartość logiczna',
        value: 'Wartość',
      },
    },
  },
  nativeCachedPhotos: {
    notifications: {
      syncFailed: {
        description: 'Są zapisane w telefonie. Przejdź w miejsce z lepszym zasięgiem i zsynchronizuj ponownie.',
        message: {
          few: 'Nie przesłano {{failedCount}} zdjęć',
          many: 'Nie przesłano {{failedCount}} zdjęć',
          one: 'Nie przesłano {{failedCount}} zdjęcia',
          other: 'Nie przesłano {{failedCount}} zdjęć',
        },
      },
    },
  },
  navigation: {
    sidebar: {
      closeAriaLabel: 'Zamknij',
    },
    userMenu: {
      belowSpecLoadEmailAlert: 'Ładunek poniżej specyfikacji',
      buyerEvidenceRequestCompletedEmailAlert: 'Zaproszenie partnera zrealizowane',
      dailyProductionSummary: 'Dzienne podsumowanie produkcji',
      dailyQualityDigest: 'Dzienny raport jakości',
      emailNotificationsEnabled: 'Powiadomienia włączone',
      language: 'Język',
      logOut: 'Wyloguj się',
      manageCompany: 'Zarządzaj firmą',
      notifications: 'E-maile z powiadomieniami',
      weeklyQualityDigest: 'Tygodniowy raport jakości',
    },
  },
  networkStatus: {
    alert: {
      offlineMessage: 'Jesteś obecnie offline. Dowody będą przechowywane lokalnie, dopóki nie wrócisz do trybu online.',
    },
    override: {
      label: 'Tryb offline',
      notifications: {
        disabled: 'Tryb offline wyłączony',
        enabled: 'Tryb offline włączony',
      },
    },
  },
  offlinePhotosAlert: {
    all: 'wszystkie',
    cacheDialog: {
      close: 'Zamknij',
      delete: 'Usuń',
      deleteError: 'Nie udało się usunąć zapisanego zdjęcia',
      deleteMessage: 'Ta operacja trwale usunie to zapisane zdjęcie i nie można jej cofnąć.',
      deleteSuccess: 'Zapisane zdjęcie usunięte',
      deleteTitle: 'Usuń zapisane zdjęcie',
      description:
        'Przejrzyj zapisane zdjęcia, pobierz to, co chcesz zachować, i usuń zdjęcia, które nie powinny być synchronizowane.',
      download: 'Pobierz',
      downloadUnavailable: 'Zdjęcie nie jest dostępne do pobrania',
      empty: 'Nie znaleziono zapisanych zdjęć.',
      nativeLoading: 'Ładowanie zapisanych zdjęć z aplikacji...',
      orderRef: 'Nr ref. zamówienia: {{orderRef}}',
      parentDeleted: 'Raport usunięty',
      parentUnavailableDescription:
        'Może się to zdarzyć, jeśli ktoś z Twojej firmy uprzątnął pustą sesję z innego konta. Usuń je albo pobierz i prześlij ponownie do właściwego raportu.',
      parentUnavailableTitle: 'Te zapisane zdjęcia nie mają już raportu',
      parentVerificationFailed: 'Nie można było sprawdzić dostępności raportu. Spróbuj ponownie później.',
      previewLoading: 'Ładowanie podglądu...',
      previewUnavailable: 'Podgląd niedostępny',
      reportId: 'Raport: {{parentId}}',
      supplier: 'Dostawca: {{supplier}}',
      takenAt: 'Zrobiono: {{takenAt}}',
      title: 'Zapisane zdjęcia',
    },
    cancel: 'Anuluj',
    deleteAction: 'Usuń {{photoNoun}}',
    deleteMessage: 'Ta operacja trwale usunie zdjęcia i nie można jej cofnąć.',
    deleteTitle: 'Usuń zdjęcia',
    descriptionWithAi:
      'Zsynchronizuj {{photoNoun}}, aby włączyć analizę AI i udostępnić {{objectPronoun}} innym użytkownikom.',
    descriptionWithoutAi:
      'Zsynchronizuj {{photoNoun}}, aby włączyć analizę i udostępnić {{objectPronoun}} innym użytkownikom.',
    it: 'je',
    notifications: {
      cachedClearError: 'Nie udało się wyczyścić zapisanych zdjęć',
      cachedClearSuccess: 'Zapisane zdjęcia wyczyszczone',
      cachedSyncError: 'Nie udało się zsynchronizować zapisanych zdjęć',
      cachedSyncSuccess: 'Zdjęcia przesłane. Analiza może potrwać kilka minut.',
      clearError: 'Nie udało się usunąć zdjęć offline',
      clearSuccess: 'Zdjęcia offline usunięte',
      syncOfflineEvidenceError: 'Nie udało się zsynchronizować dowodów offline',
      syncPartialFailure: 'Zsynchronizowano {{successCount}} zdjęć, {{failedCount}} nie powiodło się',
      syncSuccess: 'Zsynchronizowano {{successCount}} zdjęć',
      uploadError: 'Nie udało się przesłać zdjęć offline',
    },
    photo: 'zdjęcie',
    photoIs: 'zdjęcie',
    photos: 'zdjęcia',
    photosAre: 'zdjęcia',
    syncAction: 'Synchronizuj {{photoNoun}}',
    syncingMessage: 'Synchronizowanie zdjęć. Może to chwilę potrwać...',
    them: 'je',
    title: '{{count}} {{pluralVerb}} offline oczekuje na synchronizację',
    viewPhotos: 'Zobacz zdjęcia',
  },
  omnisearch: {
    noOptions: {
      withValue: 'Nie znaleziono wyników',
      withoutValue: 'Wpisz, aby wyszukać',
    },
    placeholder: {
      desktop: 'Ctrl/Cmd + K, aby wyszukać',
      mobile: 'Szukaj',
    },
  },
  siteMaterial: {
    tradeCompanySiteMaterialPicker: {
      actions: {
        addNewMaterial: 'Dodaj nowy materiał',
        addNewMaterialAriaLabel: 'Dodaj nowy materiał',
      },
      labels: {
        company: 'Firma',
        siteMaterial: 'Materiał obiektu',
      },
    },
  },
} satisfies I18nDictionaryShape<typeof featuresEnGb>;
